import {
  ArrowUpDown,
  AlertTriangle,
  BarChart3,
  Check,
  Dna,
  Download,
  Expand,
  File,
  Grid3X3,
  History,
  Image as ImageIcon,
  Quote,
  Search,
  Table2,
  X,
} from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";

import type {
  ResultArtifact,
  ResultArtifactsBlock,
  ResultBlock,
  ResultChartBlock,
  ResultDocument,
  ResultMatrixBlock,
  ResultScalar,
  ResultSequenceBlock,
  ResultTableBlock,
  ResultTimelineBlock,
} from "@rnavoyager/contracts";

import { useWorkspaceFileAccess } from "./WorkspaceFileAccess";
import { SessionFileLink, SessionMarkdown } from "./SessionFileReferences";
import {
  formatWorkspaceFileSize,
  resolveWorkspacePreviewKind,
  WorkspaceFilePreviewDialog,
} from "./WorkspaceFilePreview";
import type { TaskEvent, WorkspaceFileEntry } from "./types";
import { reportBlockIsRaw, reportReadingSections } from "@rnavoyager/domain/report";
import { ReportPage } from "./ReportPage";

const MolecularStructureViewer = lazy(() => import("./MolecularStructureViewer"));
const resultScalarFormatter = new Intl.NumberFormat("zh-CN", { maximumSignificantDigits: 8 });
const resultChartFormatter = new Intl.NumberFormat("zh-CN", { maximumSignificantDigits: 4 });
const resultTimelineFormatter = new Intl.DateTimeFormat("zh-CN", {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function rawChartTable(block: ResultChartBlock): ResultTableBlock {
  return {
    kind: "table",
    title: block.title?.replace(/ 图表$/, "") ?? "原始数据",
    derivation: "inferred",
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    columns: [
      { key: "x", label: block.xLabel ?? "横轴" },
      ...block.series.map((series, index) => ({
        key: `value_${index}`,
        label: series.label,
        ...(series.unit ? { unit: series.unit } : {}),
      })),
    ],
    rows: (block.series[0]?.points ?? []).map((point) => ({
      sourceIndex: point.sourceIndex,
      ...(point.linkKey === undefined ? {} : { linkKey: point.linkKey }),
      cells: [
        point.x,
        ...block.series.map(
          (series) =>
            series.points.find((item) => item.sourceIndex === point.sourceIndex)?.y ?? null,
        ),
      ],
    })),
    truncated: block.truncated ?? false,
  };
}
function rawMatrixTable(block: ResultMatrixBlock): ResultTableBlock {
  return {
    kind: "table",
    title: block.title?.replace(/ 热图$/, "") ?? "原始数据",
    derivation: "inferred",
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    columns: [{ key: "row", label: "行" }, ...block.columns],
    rows: block.rows.map((row) => ({
      sourceIndex: row.sourceIndex,
      ...(row.linkKey === undefined ? {} : { linkKey: row.linkKey }),
      cells: [row.label, ...row.values],
    })),
    truncated: block.truncated,
  };
}

interface ResultSelectionBase {
  sourceObservationId: string;
  sourcePath: string;
  sourceIndex: number;
  sourceLinkKey?: string | number;
  sourceIndexKind?: "item";
  sourceColumnIndex?: number;
  title: string;
  values: Array<{ label: string; value: ResultScalar }>;
}

interface ResultSelectionDraft extends ResultSelectionBase {
  detailValues?: Array<{ label: string; value: ResultScalar }>;
}

export interface ResultSelection extends ResultSelectionBase {
  documentId: string;
  documentTitle: string;
  revision: number;
}

export function TaskResultDocumentView({
  document,
  inlineFeatured = false,
  onUseSelection,
  sessionId,
  events,
  publicDemo = false,
}: {
  document: ResultDocument;
  inlineFeatured?: boolean;
  onUseSelection?: (selection: ResultSelection) => void;
  sessionId: string;
  events?: readonly TaskEvent[];
  publicDemo?: boolean;
}) {
  const documentKey = `${document.documentId}:${String(document.revision)}`;
  const [selectionState, setSelectionState] = useState<{
    documentKey: string;
    selection: ResultSelectionDraft;
  } | null>(null);
  const selection = selectionState?.documentKey === documentKey ? selectionState.selection : null;
  const tableSelectionIndex = useMemo(() => tableSelections(document), [document]);
  const artifacts = useMemo(
    () => document.blocks.flatMap((block) => (block.kind === "artifacts" ? block.items : [])),
    [document],
  );
  const resolvedSelection = selection
    ? resolveResultSelection(selection, tableSelectionIndex.get(selectionKey(selection)))
    : null;
  const selectedKey = resolvedSelection ? selectionKey(resolvedSelection) : null;
  const selectedCellKey = resolvedSelection ? cellSelectionKey(resolvedSelection) : null;

  function select(next: ResultSelectionDraft) {
    setSelectionState({ documentKey, selection: next });
  }

  return (
    <div className="result-document" data-document-id={document.documentId}>
      {resolvedSelection && (
        <ResultSelectionBar
          onClear={() => setSelectionState(null)}
          onUse={
            onUseSelection
              ? () =>
                  onUseSelection({
                    ...resolvedSelection,
                    documentId: document.documentId,
                    documentTitle: document.title,
                    revision: document.revision,
                  })
              : undefined
          }
          selection={resolvedSelection}
        />
      )}
      {document.report ? (
        <ReportPage
          publicDemo={publicDemo}
          document={document}
          sessionId={sessionId}
          draft={
            <TaskResultDocumentView
              document={withoutReport(document)}
              sessionId={sessionId}
              events={events}
              onUseSelection={onUseSelection}
            />
          }
          onSelectRow={(blockIndex, rowIndex, columnIndex) => {
            const block = document.blocks[blockIndex];
            if (block?.kind === "table" && block.rows[rowIndex])
              select(tableRowSelection(block, block.rows[rowIndex]));
            if (block?.kind === "chart") {
              const count = block.series[0]?.points.length ?? 0;
              const series = block.series[Math.floor(rowIndex / Math.max(count, 1))];
              const point = series?.points[rowIndex % Math.max(count, 1)];
              if (point)
                select({
                  sourceObservationId: block.sourceObservationId,
                  sourcePath: block.sourcePath,
                  sourceIndex: point.sourceIndex,
                  ...(point.linkKey === undefined ? {} : { sourceLinkKey: point.linkKey }),
                  title: block.title ?? "所选图表结果",
                  values: [
                    { label: block.xLabel ?? "横轴", value: point.x },
                    { label: series.label, value: point.y },
                  ],
                });
            }
            if (block?.kind === "sequence" && block.items[rowIndex])
              select(sequenceSelection(block, block.items[rowIndex]));
            if (
              block?.kind === "matrix" &&
              columnIndex !== undefined &&
              block.rows[rowIndex]?.values[columnIndex] !== undefined
            )
              select(
                matrixCellSelection(
                  block,
                  block.rows[rowIndex],
                  columnIndex,
                  block.rows[rowIndex].values[columnIndex],
                ),
              );
          }}
        />
      ) : (
        reportReadingSections(document).map((section) => {
          const content = section.blockIndices.map((index) => {
            const block = document.blocks[index];
            const raw = reportBlockIsRaw(block, document);
            const display =
              raw && block.kind === "chart"
                ? rawChartTable(block)
                : raw && block.kind === "matrix"
                  ? rawMatrixTable(block)
                  : block;
            return (
              <ResultBlockView
                block={display}
                artifacts={artifacts}
                events={events}
                inlineFeatured={inlineFeatured}
                key={`${block.kind}:${String(index)}`}
                onSelect={select}
                selectedCellKey={selectedCellKey}
                selectedKey={selectedKey}
                sessionId={sessionId}
              />
            );
          });
          return section.role === "finding" ? (
            <section key={section.sectionId}>{content}</section>
          ) : (
            <details className="report-legacy-details" key={section.sectionId}>
              <summary>{section.title}</summary>
              {content}
            </details>
          );
        })
      )}
    </div>
  );
}

function withoutReport(document: ResultDocument): ResultDocument {
  const draft = { ...document };
  delete draft.report;
  return draft;
}

function ResultBlockView({
  artifacts,
  block,
  events,
  inlineFeatured,
  onSelect,
  selectedCellKey,
  selectedKey,
  sessionId,
}: {
  artifacts: readonly ResultArtifact[];
  block: ResultBlock;
  events?: readonly TaskEvent[];
  inlineFeatured: boolean;
  onSelect: (selection: ResultSelectionDraft) => void;
  selectedCellKey: string | null;
  selectedKey: string | null;
  sessionId: string;
}) {
  switch (block.kind) {
    case "markdown":
      return (
        <article className="result-markdown">
          <SessionMarkdown sessionId={sessionId} artifacts={artifacts}>
            {block.markdown}
          </SessionMarkdown>
        </article>
      );
    case "text":
      return (
        <section className="result-block">
          <h3>{block.title ?? "文本摘录"}</h3>
          <pre className="report-text-excerpt">{block.text}</pre>
        </section>
      );
    case "metrics":
      return (
        <section aria-label={block.title ?? "关键指标"} className="result-block result-metrics">
          <ResultBlockHeading
            derivation={block.derivation ?? "inferred"}
            icon={<BarChart3 size={16} />}
            title={block.title ?? "关键指标"}
          />
          <dl>
            {block.items.map((item) => (
              <div
                key={item.sourcePath}
                title={`来源：${block.sourceObservationId}${item.sourcePath}`}
              >
                <dt>{item.label}</dt>
                <dd>
                  {typeof item.value === "string" &&
                  (item.value.includes("\n") || item.value.length > 200) ? (
                    <pre className="report-text-excerpt">{item.value}</pre>
                  ) : (
                    formatScalar(item.value)
                  )}
                  {item.unit ? <small>{item.unit}</small> : null}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      );
    case "table":
      return <ResultTable block={block} onSelect={onSelect} selectedKey={selectedKey} />;
    case "chart":
      return <ResultChart block={block} onSelect={onSelect} selectedKey={selectedKey} />;
    case "sequence":
      return <ResultSequence block={block} onSelect={onSelect} selectedKey={selectedKey} />;
    case "matrix":
      return (
        <ResultMatrix
          block={block}
          onSelect={onSelect}
          selectedCellKey={selectedCellKey}
          selectedKey={selectedKey}
        />
      );
    case "timeline":
      return <ResultTimeline block={block} />;
    case "artifacts":
      return (
        <ResultArtifacts
          block={block}
          events={events}
          inlineFeatured={inlineFeatured}
          sessionId={sessionId}
        />
      );
    case "notice":
      return (
        <section aria-label="展示视图警告" className={`result-notice ${block.level}`} role="status">
          <AlertTriangle aria-hidden="true" size={17} />
          <div>
            <strong>视图 {block.presentationId} 未展示</strong>
            <p>{block.message}</p>
            <small title={block.sourceObservationId}>来源：{block.sourceObservationId}</small>
          </div>
        </section>
      );
    case "provenance":
      return (
        <section className="result-provenance">
          <p>这些内容来自受控计算结果；预测值和计算代理不等同于实验验证或真实生物活性。</p>
          <details>
            <summary>查看计算溯源（{block.sources.length}）</summary>
            <ul>
              {block.sources.map((source) => (
                <li key={source.observationId}>
                  <code>{source.observationId}</code>
                  <span>{source.status}</span>
                  <small title={source.resultDigest}>{shortDigest(source.resultDigest)}</small>
                </li>
              ))}
            </ul>
            {block.omittedCount > 0 && <small>另有 {block.omittedCount} 条溯源记录。</small>}
          </details>
        </section>
      );
  }
}

function ResultTable({
  block,
  onSelect,
  selectedKey,
}: {
  block: ResultTableBlock;
  onSelect: (selection: ResultSelectionDraft) => void;
  selectedKey: string | null;
}) {
  const title = block.title ?? "结果表格";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{
    columnIndex: number;
    direction: "ascending" | "descending";
  } | null>(null);
  const visibleRows = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const filtered = normalizedQuery
      ? block.rows.filter((row) =>
          row.cells.some((cell) =>
            formatScalar(cell).toLocaleLowerCase().includes(normalizedQuery),
          ),
        )
      : [...block.rows];
    if (!sort) return filtered;
    return filtered.sort((left, right) => {
      const compared = compareScalars(
        left.cells[sort.columnIndex] ?? null,
        right.cells[sort.columnIndex] ?? null,
      );
      if (compared !== 0) return sort.direction === "ascending" ? compared : -compared;
      return left.sourceIndex - right.sourceIndex;
    });
  }, [block.rows, query, sort]);

  function changeSort(columnIndex: number) {
    setSort((current) =>
      current?.columnIndex === columnIndex
        ? {
            columnIndex,
            direction: current.direction === "ascending" ? "descending" : "ascending",
          }
        : { columnIndex, direction: "ascending" },
    );
  }

  return (
    <section aria-label={title} className="result-block result-table">
      <ResultBlockHeading
        derivation={block.derivation ?? "inferred"}
        icon={<Table2 size={16} />}
        title={title}
      />
      <div className="result-table-toolbar">
        <label>
          <Search aria-hidden="true" size={14} />
          <input
            aria-label={`筛选${title}`}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="筛选当前表格"
            type="search"
            value={query}
          />
        </label>
        <span aria-live="polite">
          {visibleRows.length}/{block.rows.length} 行
        </span>
      </div>
      <div className="result-table-scroll">
        <table>
          <thead>
            <tr>
              <th className="result-table-select-heading" scope="col">
                选择
              </th>
              {block.columns.map((column, columnIndex) => {
                const direction = sort?.columnIndex === columnIndex ? sort.direction : undefined;
                return (
                  <th aria-sort={direction ?? "none"} key={column.key} scope="col">
                    <button
                      aria-label={`按${column.label}排序${direction ? `，当前${direction === "ascending" ? "升序" : "降序"}` : ""}`}
                      onClick={() => changeSort(columnIndex)}
                      type="button"
                    >
                      {labelWithUnit(column.label, column.unit)}
                      <ArrowUpDown aria-hidden="true" size={12} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const rowSelection = tableRowSelection(block, row);
              const selected = selectionKey(rowSelection) === selectedKey;
              return (
                <tr className={selected ? "selected" : undefined} key={row.sourceIndex}>
                  <td className="result-table-select-cell">
                    <button
                      aria-label={`选择${title}第 ${String(row.sourceIndex + 1)} 行`}
                      aria-pressed={selected}
                      onClick={() => onSelect(rowSelection)}
                      type="button"
                    >
                      {selected ? <Check aria-hidden="true" size={13} /> : row.sourceIndex + 1}
                    </button>
                  </td>
                  {block.columns.map((column, columnIndex) => (
                    <td key={column.key}>
                      {typeof row.cells[columnIndex] === "string" &&
                      (String(row.cells[columnIndex]).includes("\n") ||
                        String(row.cells[columnIndex]).length > 200) ? (
                        <pre className="report-text-excerpt">{String(row.cells[columnIndex])}</pre>
                      ) : (
                        formatScalar(row.cells[columnIndex] ?? null)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td className="result-table-empty" colSpan={block.columns.length + 1}>
                  没有匹配的结果。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {block.truncated && <p className="result-block-note">仅展示前 100 行。</p>}
    </section>
  );
}

function ResultSequence({
  block,
  onSelect,
  selectedKey,
}: {
  block: ResultSequenceBlock;
  onSelect: (selection: ResultSelectionDraft) => void;
  selectedKey: string | null;
}) {
  const title = block.title ?? "序列与对照";
  const [lineWidth, setLineWidth] = useState<30 | 60 | 90>(60);
  const maximumLength = Math.max(...block.items.map((item) => item.sequence.length));
  const offsets = Array.from(
    { length: Math.ceil(maximumLength / lineWidth) },
    (_value, index) => index * lineWidth,
  );

  return (
    <section aria-label={title} className="result-block result-sequence">
      <ResultBlockHeading
        derivation={block.derivation ?? "inferred"}
        icon={<Dna size={16} />}
        title={title}
      />
      <div className="result-sequence-toolbar">
        <span>
          {sequenceAlphabetLabel(block.alphabet)} · {block.items.length} 条
          {block.aligned ? " · 等长对照" : ""}
        </span>
        <div aria-label="序列每行字符数" role="group">
          {([30, 60, 90] as const).map((width) => (
            <button
              aria-label={`每行 ${String(width)} 个字符`}
              aria-pressed={lineWidth === width}
              key={width}
              onClick={() => setLineWidth(width)}
              type="button"
            >
              {width}
            </button>
          ))}
        </div>
      </div>
      <p className="result-sequence-normalization">
        展示时移除空白并统一为大写；原始 Observation 不会被修改。
      </p>
      <div className="result-sequence-scroll">
        {offsets.map((offset, segmentIndex) => {
          const end = Math.min(maximumLength, offset + lineWidth);
          return (
            <div className="result-sequence-segment" key={offset}>
              <div aria-hidden="true" className="result-sequence-ruler">
                <span>{offset + 1}</span>
                <i />
                <span>{end}</span>
              </div>
              {block.items.map((item) => {
                const itemSelection = sequenceSelection(block, item);
                const selected = selectionKey(itemSelection) === selectedKey;
                const chunk = item.sequence.slice(offset, offset + lineWidth);
                return (
                  <div
                    className={`result-sequence-row${selected ? " selected" : ""}`}
                    key={`${item.sourceIndex}:${String(offset)}`}
                  >
                    {segmentIndex === 0 ? (
                      <button
                        aria-label={`选择序列 ${item.label}`}
                        aria-pressed={selected}
                        onClick={() => onSelect(itemSelection)}
                        title={item.label}
                        type="button"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span title={item.label}>{item.label}</span>
                    )}
                    <code>{groupSequenceChunk(chunk)}</code>
                    <small>{Math.min(item.sequence.length, end)}</small>
                  </div>
                );
              })}
              {block.aligned && block.items.length > 1 && (
                <div className="result-sequence-row consensus">
                  <span>一致性</span>
                  <code>{groupSequenceChunk(sequenceConservation(block.items, offset, end))}</code>
                  <small />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {(block.omittedCount > 0 || block.items.some((item) => item.truncated)) && (
        <p className="result-block-note">
          {block.omittedCount > 0 ? `另有 ${String(block.omittedCount)} 条序列未展示。` : ""}
          {block.items.some((item) => item.truncated)
            ? " 超长序列只展示前 4096 个字符，完整内容仍保留在来源结果中。"
            : ""}
        </p>
      )}
    </section>
  );
}

function ResultMatrix({
  block,
  onSelect,
  selectedCellKey,
  selectedKey,
}: {
  block: ResultMatrixBlock;
  onSelect: (selection: ResultSelectionDraft) => void;
  selectedCellKey: string | null;
  selectedKey: string | null;
}) {
  const title = block.title ?? "数值矩阵";
  const values = block.rows.flatMap((row) => row.values);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const diverging = minimum < 0 && maximum > 0;

  return (
    <section aria-label={title} className="result-block result-matrix">
      <ResultBlockHeading
        derivation={block.derivation ?? "inferred"}
        icon={<Grid3X3 size={16} />}
        title={title}
      />
      <div className="result-matrix-legend">
        <span>{formatChartNumber(minimum)}</span>
        <i className={diverging ? "diverging" : "sequential"} />
        <span>{formatChartNumber(maximum)}</span>
      </div>
      <div className="result-matrix-scroll">
        <table aria-label={`${title}数据`}>
          <thead>
            <tr>
              <th scope="col">行</th>
              {block.columns.map((column) => (
                <th key={column.key} scope="col" title={column.label}>
                  {labelWithUnit(column.label, column.unit)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => {
              const rowKey = selectionKey({
                sourceObservationId: block.sourceObservationId,
                sourcePath: block.sourcePath,
                sourceIndex: row.sourceIndex,
                ...(row.linkKey === undefined ? {} : { sourceLinkKey: row.linkKey }),
              });
              return (
                <tr
                  className={rowKey === selectedKey ? "selected" : undefined}
                  key={row.sourceIndex}
                >
                  <th scope="row" title={row.label}>
                    {row.label}
                  </th>
                  {block.columns.map((column, columnIndex) => {
                    const value = row.values[columnIndex];
                    if (value === undefined) return <td key={column.key}>—</td>;
                    const cellSelection = matrixCellSelection(block, row, columnIndex, value);
                    const selected = cellSelectionKey(cellSelection) === selectedCellKey;
                    const background = heatmapColor(value, minimum, maximum, diverging);
                    return (
                      <td key={column.key}>
                        <button
                          aria-label={`选择 ${row.label}、${column.label}：${formatChartNumber(value)}`}
                          aria-pressed={selected}
                          onClick={() => onSelect(cellSelection)}
                          style={{
                            backgroundColor: background,
                            color: heatmapTextColor(background),
                          }}
                          title={`${row.label} · ${column.label} · ${formatChartNumber(value)}`}
                          type="button"
                        >
                          {formatChartNumber(value)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {block.truncated && <p className="result-block-note">矩阵过大，仅展示前 48 行与前 24 列。</p>}
    </section>
  );
}

function ResultTimeline({ block }: { block: ResultTimelineBlock }) {
  const title = block.title ?? "计算时间线";
  return (
    <section aria-label={title} className="result-block result-timeline">
      <ResultBlockHeading icon={<History size={16} />} title={title} />
      <ol>
        {block.items.map((item, index) => (
          <li className={item.status} key={item.observationId}>
            <i aria-hidden="true" />
            <div>
              <strong>
                计算 {index + 1} · {observationStatusLabel(item.status)}
              </strong>
              <code title={item.actionId}>{item.actionId}</code>
              <small title={item.observationId}>{item.observationId}</small>
            </div>
            <time dateTime={item.completedAt}>{formatTimelineTime(item.completedAt)}</time>
          </li>
        ))}
      </ol>
      {block.omittedCount > 0 && (
        <p className="result-block-note">另有 {block.omittedCount} 条计算记录未展示。</p>
      )}
    </section>
  );
}

function ResultSelectionBar({
  onClear,
  onUse,
  selection,
}: {
  onClear: () => void;
  onUse?: () => void;
  selection: ResultSelectionBase;
}) {
  return (
    <section aria-label="已选结果" aria-live="polite" className="result-selection-bar">
      <header>
        <div>
          <strong>{selection.title}</strong>
          <small title={selection.sourceObservationId}>
            {selection.sourcePath} · 来源{selection.sourceIndexKind === "item" ? "项" : "行"}{" "}
            {selection.sourceIndex + 1}
            {selection.sourceColumnIndex === undefined
              ? ""
              : ` · 来源列 ${String(selection.sourceColumnIndex + 1)}`}
          </small>
        </div>
        <button aria-label="清除结果选择" onClick={onClear} title="清除选择" type="button">
          <X size={14} />
        </button>
      </header>
      <dl>
        {selection.values.map((item, index) => (
          <div key={`${item.label}:${String(index)}`}>
            <dt>{item.label}</dt>
            <dd>{formatScalar(item.value)}</dd>
          </div>
        ))}
      </dl>
      {onUse && (
        <button className="primary result-selection-use" onClick={onUse} type="button">
          <Quote aria-hidden="true" size={14} /> 引用到消息
        </button>
      )}
    </section>
  );
}

function ResultBlockHeading({
  derivation,
  icon,
  title,
}: {
  derivation?: "declared" | "inferred" | "selected";
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <header className="result-block-heading">
      {icon}
      <h3>{title}</h3>
      {derivation && (
        <small className={`result-derivation ${derivation}`}>
          {derivation === "declared"
            ? "合同声明"
            : derivation === "selected"
              ? "所选证据"
              : "原始数据"}
        </small>
      )}
    </header>
  );
}

function ResultArtifacts({
  block,
  events,
  inlineFeatured,
  sessionId,
}: {
  block: ResultArtifactsBlock;
  events?: readonly TaskEvent[];
  inlineFeatured: boolean;
  sessionId: string;
}) {
  const previewCandidates = block.items.filter(directlyEmbeddableArtifact);
  const initialArtifact = block.items.find(
    (artifact) => artifact.featured && directlyEmbeddableArtifact(artifact),
  );
  const [focusedArtifactId, setFocusedArtifactId] = useState<string | null>(null);
  const focusedArtifact =
    previewCandidates.find((artifact) => artifact.artifactId === focusedArtifactId) ??
    (inlineFeatured ? initialArtifact : undefined);
  const artifactSections = artifactGroupSections(block);

  return (
    <section aria-label={block.title ?? "结果文件"} className="result-block result-artifacts">
      <ResultBlockHeading
        derivation={block.derivation ?? "inferred"}
        icon={<File size={16} />}
        title={block.title ?? "结果文件"}
      />
      {focusedArtifact && (
        <>
          {previewCandidates.length > 1 && (
            <div className="result-artifact-switcher">
              <label>
                单实例快速预览
                <select
                  aria-label="选择快速预览文件"
                  onChange={(event) => setFocusedArtifactId(event.target.value)}
                  value={focusedArtifact.artifactId}
                >
                  {previewCandidates.map((artifact) => (
                    <option key={artifact.artifactId} value={artifact.artifactId}>
                      {artifact.name}
                    </option>
                  ))}
                </select>
              </label>
              <span>
                {previewCandidates.findIndex(
                  (artifact) => artifact.artifactId === focusedArtifact.artifactId,
                ) + 1}
                /{previewCandidates.length}
              </span>
            </div>
          )}
          <ResultArtifactInline artifact={focusedArtifact} sessionId={sessionId} />
        </>
      )}
      {artifactSections.map((section, sectionIndex) => (
        <div className="result-artifact-section" key={`${section.title}:${String(sectionIndex)}`}>
          {section.title && <h4>{section.title}</h4>}
          <div className="result-artifact-grid">
            {section.items.map((artifact) => (
              <ResultArtifactCard
                artifact={artifact}
                events={events}
                focused={focusedArtifact?.artifactId === artifact.artifactId}
                key={artifact.artifactId}
                onFocus={
                  directlyEmbeddableArtifact(artifact)
                    ? () => setFocusedArtifactId(artifact.artifactId)
                    : undefined
                }
                sessionId={sessionId}
              />
            ))}
          </div>
        </div>
      ))}
      {block.omittedCount > 0 && (
        <p className="result-block-note">
          另有 {block.omittedCount} 个文件，请在会话文件栏中查看。
        </p>
      )}
    </section>
  );
}

function artifactGroupSections(block: ResultArtifactsBlock): Array<{
  title: string | null;
  items: ResultArtifact[];
}> {
  if (!block.groups || block.groups.length === 0) return [{ title: null, items: block.items }];
  const byId = new Map(block.items.map((artifact) => [artifact.artifactId, artifact]));
  const grouped = new Set<string>();
  const sections = block.groups.flatMap((group) => {
    const items = group.artifactIds.flatMap((artifactId) => {
      const artifact = byId.get(artifactId);
      if (!artifact || grouped.has(artifactId)) return [];
      grouped.add(artifactId);
      return [artifact];
    });
    return items.length > 0 ? [{ title: group.title, items }] : [];
  });
  const remaining = block.items.filter((artifact) => !grouped.has(artifact.artifactId));
  if (remaining.length > 0) sections.push({ title: "其他结果文件", items: remaining });
  return sections;
}

function ResultArtifactInline({
  artifact,
  sessionId,
}: {
  artifact: ResultArtifact;
  sessionId: string;
}) {
  const entry = artifactEntry(artifact);
  const { contentUrl, downloadUrl } = useWorkspaceFileAccess();
  const kind = resolveWorkspacePreviewKind(entry);
  return (
    <div className={`result-artifact-focus ${kind}`}>
      <header>
        <strong title={artifact.name}>{artifact.name}</strong>
        <small>切换文件时复用同一个预览位置</small>
      </header>
      {kind === "image" && (
        <div className="result-artifact-inline image">
          <img
            alt={`${artifact.name} 结果预览`}
            key={artifact.artifactId}
            loading="lazy"
            src={contentUrl(sessionId, entry.path)}
          />
        </div>
      )}
      {kind === "structure" && (
        <div className="result-artifact-inline structure">
          <Suspense fallback={<p className="result-inline-loading">正在加载三维查看器…</p>}>
            <MolecularStructureViewer
              key={artifact.artifactId}
              name={artifact.name}
              url={downloadUrl(sessionId, entry.path)}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

function ResultArtifactCard({
  artifact,
  events,
  focused,
  onFocus,
  sessionId,
}: {
  artifact: ResultArtifact;
  events?: readonly TaskEvent[];
  focused: boolean;
  onFocus?: () => void;
  sessionId: string;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const { downloadUrl } = useWorkspaceFileAccess();
  const entry = useMemo(() => artifactEntry(artifact), [artifact]);
  const kind = resolveWorkspacePreviewKind(entry);
  // 优先保留末尾的样本编号和扩展名；完整名称仍用于提示、预览与下载。
  const nameCharacters = Array.from(artifact.name);
  let splitAt = nameCharacters.length > 32 ? nameCharacters.length - 24 : nameCharacters.length;
  if (splitAt < nameCharacters.length) {
    const separator = nameCharacters.lastIndexOf("_", splitAt);
    if (separator >= 8 && splitAt - separator <= 8) splitAt = separator + 1;
  }
  const nameStart = nameCharacters.slice(0, splitAt).join("");
  const nameTail = nameCharacters.slice(splitAt).join("");
  return (
    <article className={`result-artifact-card${focused ? " focused" : ""}`}>
      <header>
        <span aria-hidden="true" className="result-artifact-icon">
          {kind === "image" ? <ImageIcon size={17} /> : <File size={17} />}
        </span>
        <div>
          <strong className="result-artifact-name" title={artifact.name} aria-label={artifact.name}>
            <span className="result-artifact-name-start">{nameStart}</span>
            {nameTail && <span className="result-artifact-name-tail">{nameTail}</span>}
          </strong>
          <small title={`${formatWorkspaceFileSize(artifact.sizeBytes)} · ${artifact.mediaType}`}>
            {formatWorkspaceFileSize(artifact.sizeBytes)} · {artifact.mediaType}
          </small>
        </div>
      </header>
      <details className="result-artifact-location">
        <summary>文件位置</summary>
        <SessionFileLink
          path={artifact.path}
          sessionId={sessionId}
          onOpen={() => setPreviewOpen(true)}
        />
      </details>
      <footer className="result-artifact-actions">
        {onFocus && (
          <button disabled={focused} onClick={onFocus} type="button">
            {focused ? "正在查看" : "快速预览"}
          </button>
        )}
        {kind !== "unsupported" && (
          <button type="button" onClick={() => setPreviewOpen(true)}>
            <Expand size={14} /> 展开
          </button>
        )}
        <a download={artifact.name} href={downloadUrl(sessionId, entry.path)}>
          <Download size={14} /> 下载
        </a>
      </footer>
      {previewOpen && (
        <WorkspaceFilePreviewDialog
          entry={entry}
          events={events}
          onClose={() => setPreviewOpen(false)}
          sessionId={sessionId}
        />
      )}
    </article>
  );
}

function ResultChart({
  block,
  onSelect,
  selectedKey,
}: {
  block: ResultChartBlock;
  onSelect: (selection: ResultSelectionDraft) => void;
  selectedKey: string | null;
}) {
  const width = 680;
  const height = 280;
  const inset = { top: 22, right: 20, bottom: 48, left: 58 };
  const plotWidth = width - inset.left - inset.right;
  const plotHeight = height - inset.top - inset.bottom;
  const points = block.series.flatMap((series) => series.points);
  const yValues = points.map((point) => point.y);
  const rawYMinimum = Math.min(...yValues);
  const rawYMaximum = Math.max(...yValues);
  const yMinimum = block.chartType === "bar" ? Math.min(0, rawYMinimum) : rawYMinimum;
  const yMaximum = block.chartType === "bar" ? Math.max(0, rawYMaximum) : rawYMaximum;
  const yPadding = yMinimum === yMaximum ? Math.max(1, Math.abs(yMinimum) * 0.1) : 0;
  const yLow = yMinimum - yPadding;
  const yHigh = yMaximum + yPadding;
  const numericX = points.every((point) => typeof point.x === "number");
  const xValues = numericX ? points.map((point) => Number(point.x)) : [];
  const xMinimum = numericX ? Math.min(...xValues) : 0;
  const xMaximum = numericX ? Math.max(...xValues) : 0;
  const categories = [...new Set(points.map((point) => String(point.x)))];
  const xTicks: Array<string | number> = numericX
    ? [...new Set([xMinimum, xMaximum])]
    : sampledValues(categories, 6);
  const xPosition = (value: string | number) => {
    if (numericX) {
      if (xMinimum === xMaximum) return inset.left + plotWidth / 2;
      return inset.left + ((Number(value) - xMinimum) / (xMaximum - xMinimum)) * plotWidth;
    }
    const index = Math.max(0, categories.indexOf(String(value)));
    return inset.left + ((index + 0.5) / Math.max(1, categories.length)) * plotWidth;
  };
  const yPosition = (value: number) =>
    inset.top + (1 - (value - yLow) / Math.max(Number.EPSILON, yHigh - yLow)) * plotHeight;
  const colors = ["#147a59", "#5777b8", "#ba6d3c", "#8d5aa7", "#3f8996", "#a44f5c"];
  const ticks = Array.from({ length: 5 }, (_, index) => yLow + ((yHigh - yLow) * index) / 4);
  const xAxisLabel = block.xLabel ? labelWithUnit(block.xLabel, block.xUnit) : null;
  const yAxisLabel = block.yLabel
    ? labelWithUnit(block.yLabel, block.series.length === 1 ? block.series[0]?.unit : undefined)
    : null;

  return (
    <section aria-label={block.title ?? "结果图表"} className="result-block result-chart">
      <ResultBlockHeading
        derivation={block.derivation ?? "inferred"}
        icon={<BarChart3 size={16} />}
        title={block.title ?? "结果图表"}
      />
      <svg
        aria-label={`${block.title ?? "结果"}图表`}
        className="result-chart-svg"
        preserveAspectRatio="xMidYMid meet"
        role="group"
        viewBox={`0 0 ${String(width)} ${String(height)}`}
      >
        <desc>
          {block.series
            .map((series) => `${series.label} ${series.points.length} 个数据点`)
            .join("；")}
        </desc>
        {ticks.map((tick) => {
          const y = yPosition(tick);
          return (
            <g key={tick}>
              <line
                className="result-chart-gridline"
                x1={inset.left}
                x2={width - inset.right}
                y1={y}
                y2={y}
              />
              <text className="result-chart-tick" textAnchor="end" x={inset.left - 9} y={y + 4}>
                {formatChartNumber(tick)}
              </text>
            </g>
          );
        })}
        <line
          className="result-chart-axis"
          x1={inset.left}
          x2={inset.left}
          y1={inset.top}
          y2={height - inset.bottom}
        />
        <line
          className="result-chart-axis"
          x1={inset.left}
          x2={width - inset.right}
          y1={height - inset.bottom}
          y2={height - inset.bottom}
        />
        {xTicks.map((tick) => {
          const x = xPosition(tick);
          return (
            <g key={String(tick)}>
              <line
                className="result-chart-axis"
                x1={x}
                x2={x}
                y1={height - inset.bottom}
                y2={height - inset.bottom + 5}
              />
              <text
                className="result-chart-tick"
                textAnchor="middle"
                x={x}
                y={height - inset.bottom + 17}
              >
                {typeof tick === "number" ? formatChartNumber(tick) : boundedLabel(tick, 12)}
              </text>
            </g>
          );
        })}
        {block.series.map((series, seriesIndex) => {
          const color = colors[seriesIndex % colors.length];
          const polyline = series.points
            .map((point) => `${String(xPosition(point.x))},${String(yPosition(point.y))}`)
            .join(" ");
          return (
            <g key={`${series.label}:${String(seriesIndex)}`}>
              {block.chartType === "line" && (
                <polyline fill="none" points={polyline} stroke={color} strokeWidth="2.5" />
              )}
              {series.points.map((point, pointIndex) => {
                const x = xPosition(point.x);
                const y = yPosition(point.y);
                const pointSelection = chartPointSelection(
                  block,
                  labelWithUnit(series.label, series.unit),
                  point.sourceIndex,
                  point.linkKey,
                  point.x,
                  point.y,
                );
                const selected = selectionKey(pointSelection) === selectedKey;
                const label = chartPointLabel(block, series.label, point.x, point.y);
                const handleKeyDown = (event: React.KeyboardEvent<SVGElement>) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  onSelect(pointSelection);
                };
                if (block.chartType === "bar") {
                  const groupWidth = plotWidth / Math.max(1, categories.length || points.length);
                  const barWidth = Math.max(3, (groupWidth * 0.68) / block.series.length);
                  const zeroY = yPosition(Math.max(yLow, Math.min(yHigh, 0)));
                  return (
                    <rect
                      aria-label={`选择 ${label}`}
                      aria-pressed={selected}
                      className={`result-chart-point${selected ? " selected" : ""}`}
                      fill={color}
                      height={Math.abs(zeroY - y)}
                      key={`${String(point.x)}:${String(pointIndex)}`}
                      onClick={() => onSelect(pointSelection)}
                      onKeyDown={handleKeyDown}
                      role="button"
                      rx="2"
                      tabIndex={0}
                      width={barWidth}
                      x={x - (barWidth * block.series.length) / 2 + barWidth * seriesIndex}
                      y={Math.min(y, zeroY)}
                    >
                      <title>{label}</title>
                    </rect>
                  );
                }
                return (
                  <circle
                    aria-label={`选择 ${label}`}
                    aria-pressed={selected}
                    className={`result-chart-point${selected ? " selected" : ""}`}
                    cx={x}
                    cy={y}
                    fill={color}
                    key={`${String(point.x)}:${String(pointIndex)}`}
                    onClick={() => onSelect(pointSelection)}
                    onKeyDown={handleKeyDown}
                    r={block.chartType === "scatter" ? 4.5 : 3.5}
                    role="button"
                    tabIndex={0}
                  >
                    <title>{label}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
        {xAxisLabel && (
          <text
            aria-label={xAxisLabel}
            className="result-chart-label"
            textAnchor="middle"
            x={inset.left + plotWidth / 2}
            y={height - 8}
          >
            {boundedLabel(xAxisLabel, 48)}
          </text>
        )}
        {yAxisLabel && (
          <text
            aria-label={yAxisLabel}
            className="result-chart-label"
            textAnchor="middle"
            transform={`rotate(-90 14 ${String(inset.top + plotHeight / 2)})`}
            x="14"
            y={inset.top + plotHeight / 2}
          >
            {boundedLabel(yAxisLabel, 18)}
          </text>
        )}
      </svg>
      <div aria-label="图例" className="result-chart-legend">
        {block.series.map((series, index) => (
          <span key={`${series.label}:${String(index)}`}>
            <i style={{ backgroundColor: colors[index % colors.length] }} />{" "}
            {labelWithUnit(series.label, series.unit)}
          </span>
        ))}
      </div>
      {block.truncated && <p className="result-block-note">图表仅展示前 256 个来源项。</p>}
    </section>
  );
}

function resolveResultSelection(
  selection: ResultSelectionDraft,
  linkedTableRow: ResultSelectionDraft | undefined,
): ResultSelectionBase {
  const detailValues = selection.detailValues ?? [];
  const exactLinkedTableRow =
    linkedTableRow?.sourcePath === selection.sourcePath &&
    linkedTableRow.sourceIndex === selection.sourceIndex
      ? linkedTableRow
      : undefined;
  const values = exactLinkedTableRow
    ? [...exactLinkedTableRow.values, ...detailValues]
    : [...selection.values, ...detailValues];
  return {
    sourceObservationId: selection.sourceObservationId,
    sourcePath: selection.sourcePath,
    sourceIndex: selection.sourceIndex,
    ...(selection.sourceLinkKey === undefined ? {} : { sourceLinkKey: selection.sourceLinkKey }),
    ...(!exactLinkedTableRow && selection.sourceIndexKind === "item"
      ? { sourceIndexKind: "item" as const }
      : {}),
    ...(selection.sourceColumnIndex === undefined
      ? {}
      : { sourceColumnIndex: selection.sourceColumnIndex }),
    title: exactLinkedTableRow?.title ?? selection.title,
    values,
  };
}

function sequenceSelection(
  block: ResultSequenceBlock,
  item: ResultSequenceBlock["items"][number],
): ResultSelectionDraft {
  return {
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    sourceIndex: item.sourceIndex,
    ...(item.linkKey === undefined ? {} : { sourceLinkKey: item.linkKey }),
    sourceIndexKind: "item",
    title: block.title ?? "序列与对照",
    values: [
      { label: "名称", value: item.label },
      { label: "序列", value: item.sequence },
    ],
    detailValues: [
      { label: "完整长度", value: item.totalLength },
      ...(item.truncated ? [{ label: "展示已截断", value: true }] : []),
    ],
  };
}

function matrixCellSelection(
  block: ResultMatrixBlock,
  row: ResultMatrixBlock["rows"][number],
  columnIndex: number,
  value: number,
): ResultSelectionDraft {
  return {
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    sourceIndex: row.sourceIndex,
    ...(row.linkKey === undefined ? {} : { sourceLinkKey: row.linkKey }),
    sourceColumnIndex: columnIndex,
    title: block.title ?? "数值矩阵",
    values: [],
    detailValues: [
      { label: "矩阵行", value: row.label },
      {
        label: "矩阵列",
        value: block.columns[columnIndex]
          ? labelWithUnit(block.columns[columnIndex]!.label, block.columns[columnIndex]!.unit)
          : columnIndex + 1,
      },
      { label: "矩阵值", value },
    ],
  };
}

function sequenceAlphabetLabel(alphabet: ResultSequenceBlock["alphabet"]): string {
  switch (alphabet) {
    case "rna":
      return "RNA";
    case "dna":
      return "DNA";
    case "nucleic_acid":
      return "核酸";
    case "protein":
      return "蛋白质";
  }
}

function groupSequenceChunk(value: string): string {
  return value.match(/.{1,10}/gu)?.join(" ") ?? "";
}

function sequenceConservation(
  items: ResultSequenceBlock["items"],
  start: number,
  end: number,
): string {
  let output = "";
  for (let index = start; index < end; index += 1) {
    const first = items[0]?.sequence[index];
    output +=
      first && first !== "-" && items.every((item) => item.sequence[index] === first) ? "*" : "·";
  }
  return output;
}

function heatmapColor(value: number, minimum: number, maximum: number, diverging: boolean): string {
  const neutral: readonly [number, number, number] = [247, 248, 244];
  if (diverging) {
    if (value < 0) {
      return interpolateColor(
        neutral,
        [53, 101, 153],
        clamp01(value / Math.min(-Number.EPSILON, minimum)),
      );
    }
    return interpolateColor(
      neutral,
      [181, 73, 64],
      clamp01(value / Math.max(Number.EPSILON, maximum)),
    );
  }
  const position = minimum === maximum ? 0.5 : clamp01((value - minimum) / (maximum - minimum));
  return interpolateColor([237, 247, 242], [22, 112, 82], position);
}

function interpolateColor(
  start: readonly [number, number, number],
  end: readonly [number, number, number],
  position: number,
): string {
  const channels = start.map((channel, index) =>
    Math.round(channel + ((end[index] ?? channel) - channel) * position),
  );
  return `rgb(${String(channels[0])} ${String(channels[1])} ${String(channels[2])})`;
}

function heatmapTextColor(background: string): string {
  const channels = background.match(/\d+/gu)?.map(Number) ?? [];
  const luminance =
    (channels[0] ?? 255) * 0.299 + (channels[1] ?? 255) * 0.587 + (channels[2] ?? 255) * 0.114;
  return luminance < 145 ? "#fff" : "#243a32";
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function observationStatusLabel(status: ResultTimelineBlock["items"][number]["status"]): string {
  switch (status) {
    case "succeeded":
      return "已完成";
    case "failed":
      return "失败";
    case "cancelled":
      return "已取消";
    case "timed_out":
      return "超时";
  }
}

function formatTimelineTime(value: string): string {
  return resultTimelineFormatter.format(new Date(value));
}

function tableSelections(document: ResultDocument): Map<string, ResultSelectionDraft> {
  const selections = new Map<string, ResultSelectionDraft>();
  for (const block of document.blocks) {
    if (block.kind !== "table") continue;
    for (const row of block.rows) {
      const selection = tableRowSelection(block, row);
      selections.set(selectionKey(selection), selection);
    }
  }
  return selections;
}

function tableRowSelection(
  block: ResultTableBlock,
  row: ResultTableBlock["rows"][number],
): ResultSelectionDraft {
  return {
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    sourceIndex: row.sourceIndex,
    ...(row.linkKey === undefined ? {} : { sourceLinkKey: row.linkKey }),
    title: block.title ?? "结果表格",
    values: block.columns.map((column, index) => ({
      label: labelWithUnit(column.label, column.unit),
      value: row.cells[index] ?? null,
    })),
  };
}

function chartPointSelection(
  block: ResultChartBlock,
  seriesLabel: string,
  sourceIndex: number,
  sourceLinkKey: string | number | undefined,
  x: string | number,
  y: number,
): ResultSelectionDraft {
  return {
    sourceObservationId: block.sourceObservationId,
    sourcePath: block.sourcePath,
    sourceIndex,
    ...(sourceLinkKey === undefined ? {} : { sourceLinkKey }),
    title: block.title ?? "结果图表",
    values: [
      { label: labelWithUnit(block.xLabel ?? "X", block.xUnit), value: x },
      { label: seriesLabel, value: y },
    ],
  };
}

function selectionKey(
  selection: Pick<
    ResultSelectionDraft,
    "sourceObservationId" | "sourcePath" | "sourceIndex" | "sourceLinkKey"
  >,
): string {
  if (selection.sourceLinkKey !== undefined) {
    return `${selection.sourceObservationId}\u0000link\u0000${typeof selection.sourceLinkKey}:${String(selection.sourceLinkKey)}`;
  }
  return `${selection.sourceObservationId}\u0000${selection.sourcePath}\u0000${String(selection.sourceIndex)}`;
}

function cellSelectionKey(
  selection: Pick<
    ResultSelectionBase,
    "sourceObservationId" | "sourcePath" | "sourceIndex" | "sourceLinkKey" | "sourceColumnIndex"
  >,
): string | null {
  return selection.sourceColumnIndex === undefined
    ? null
    : `${selectionKey(selection)}\u0000${String(selection.sourceColumnIndex)}`;
}

function compareScalars(left: ResultScalar, right: ResultScalar): number {
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (left === null && right !== null) return 1;
  if (left !== null && right === null) return -1;
  return formatScalar(left).localeCompare(formatScalar(right), "zh-CN", {
    numeric: true,
    sensitivity: "base",
  });
}

function artifactEntry(artifact: ResultArtifact): WorkspaceFileEntry {
  return {
    path: `/project/${artifact.path}`,
    name: artifact.name,
    kind: "file",
    size: artifact.sizeBytes,
    modified_at: null,
    media_type: artifact.mediaType,
    previewable: artifactPreviewable(artifact),
  };
}

function artifactPreviewable(artifact: ResultArtifact): boolean {
  const mediaType = artifact.mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return (
    ["image/gif", "image/jpeg", "image/png", "image/webp"].includes(mediaType) ||
    mediaType.startsWith("text/") ||
    ["application/json", "application/xml", "application/yaml"].includes(mediaType) ||
    /\.(?:bcif|cif|ent|gro|mmcif|mol|mol2|pdb|pdbqt|pqr|sdf|xyz)(?:\.gz)?$/iu.test(artifact.name)
  );
}

function directlyEmbeddableArtifact(artifact: ResultArtifact): boolean {
  if (artifact.sizeBytes > 25 * 1024 * 1024) return false;
  const kind = resolveWorkspacePreviewKind(artifactEntry(artifact));
  return kind === "image" || kind === "structure";
}

function formatScalar(value: ResultScalar): string {
  if (value === null) return "—";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "number") return resultScalarFormatter.format(value);
  return value;
}

function labelWithUnit(label: string, unit: string | undefined): string {
  return unit ? `${label}（${unit}）` : label;
}

function formatChartNumber(value: number): string {
  return resultChartFormatter.format(value);
}

function sampledValues<T>(values: readonly T[], maximum: number): T[] {
  if (values.length <= maximum) return [...values];
  const indices = Array.from({ length: maximum }, (_, index) =>
    Math.round((index * (values.length - 1)) / (maximum - 1)),
  );
  return [...new Set(indices)].map((index) => values[index]!);
}

function boundedLabel(value: string, maximumCharacters: number): string {
  return value.length <= maximumCharacters ? value : `${value.slice(0, maximumCharacters - 1)}…`;
}

function chartPointLabel(
  block: ResultChartBlock,
  seriesLabel: string,
  x: string | number,
  y: number,
): string {
  return `${seriesLabel}；${block.xLabel ?? "X"}：${String(x)}；${block.yLabel ?? "值"}：${formatChartNumber(y)}`;
}

function shortDigest(value: string): string {
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}
